import React, { FunctionComponent } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { ArrayUtils } from 'record-it-utils/utils/ArrayUtils/ArrayUtils';
import { RoutePaths } from '../classes/RoutePaths/RoutePaths';
import { DefaultLayout } from '../components/layout/DefaultLayout/DefaultLayout';
import { LoginLayout } from '../components/layout/LoginLayout/LoginLayout';
import { useAuth } from '../hooks/useAuth';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { AppRouteElementWrapper } from './AppRouteElementWrapper';
import {
  AuthenticatedPermission,
  LayoutType,
  NavigationConfigurationPermissions,
  navigationConfigurations,
  NavigationConfigurationWithKey
} from './navigationConfigurations';

export const AppRoutes: FunctionComponent<Record<string, never>> = React.memo(
  () => {
    const { isLoggedIn } = useAuth();
    const { currentUser } = useCurrentUser();

    // we are logged in but there is no current user yet, wait for it
    if (!currentUser && isLoggedIn) {
      return null;
    }

    const rootPath = isLoggedIn ? RoutePaths.home() : RoutePaths.loginPage();

    const configsWithPermission: Array<NavigationConfigurationWithKey> =
      navigationConfigurations.filter((config) => {
        switch (config.permissions.authenticated) {
          case AuthenticatedPermission.All:
            return true;

          case AuthenticatedPermission.OnlyUnauthenticated:
            return !isLoggedIn;

          case AuthenticatedPermission.OnlyAuthenticated:
            if (!isLoggedIn) {
              return false;
            }

            return true;

          default:
            console.error(
              `branch for ${(config.permissions as NavigationConfigurationPermissions).authenticated} not implemented`
            );
            return false;
        }
      });

    const configsByLayout = ArrayUtils.group(
      configsWithPermission,
      (config) => config.layoutType
    );
    const configGroups = Array.from(configsByLayout.entries()).map(
      ([layoutType, configs]) => ({ layoutType, configs })
    );

    const getLayoutElement = (layoutType: LayoutType): JSX.Element => {
      switch (layoutType) {
        case LayoutType.Default:
          return <DefaultLayout></DefaultLayout>;

        case LayoutType.Login:
          return <LoginLayout></LoginLayout>;

        default:
          throw new Error(`unknown layoutType: ${layoutType}`);
      }
    };

    return (
      <BrowserRouter>
        <Routes>
          {configGroups.map((group) => (
            <Route
              element={getLayoutElement(group.layoutType)}
              key={group.layoutType}
            >
              {group.configs.map((config) => (
                <Route
                  key={config.key}
                  {...config.routeProps}
                  element={
                    <AppRouteElementWrapper
                      config={config}
                    ></AppRouteElementWrapper>
                  }
                ></Route>
              ))}
            </Route>
          ))}

          <Route
            path="*"
            element={
              <Navigate
                to={rootPath}
                replace
              ></Navigate>
            }
          ></Route>
        </Routes>
      </BrowserRouter>
    );
  }
);
